import { EventTracker } from '@/utils';
import { cn } from '@/utils/styles';
import { Card, CardContent } from '@design-system/Card';
import { Skeleton } from '@design-system/Skeleton';
import { LabProcessSheet } from '@screens/HomePage/components/LabProcessSheet';
import { StepProgressBar } from '@screens/HomePage/components/StepProgressBar';
import { useGetTrackV3 } from '@viome/viome-base/src/usecase/Home/HomeV2Query';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface HomeTrackProgressProps {
  screen: 'nornr' | 'retest';
}

export const HomeTrackProgressV2 = ({ screen }: HomeTrackProgressProps) => {
  const [isLabProcessOpen, setIsLabProcessOpen] = useState(false);
  const { data: testStatus, isLoading, isError } = useGetTrackV3();

  const isRetestMode = screen === 'retest';

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className={cn('h-8 w-48', isRetestMode && 'rounded-lg bg-white/10')} />
        <Skeleton className={cn('h-52 w-full', isRetestMode && 'rounded-lg bg-white/10')} />
      </div>
    );
  }

  if (isError || !testStatus || testStatus.simplified_status === 'RESULTS_READY') {
    return null;
  }

  const issueInCurrentStatus = testStatus.is_issue_in_current_status;
  const currentStepIndex = testStatus.track_status_id - 1;

  return (
    <>
      <div className="space-y-4">
        <Card
          data-testid="home-trackProgress-card"
          className={cn(
            'relative border-0',
            isRetestMode
              ? [
                  'bg-[#5B5C644D]',
                  'backdrop-blur-[50px]',
                  'shadow-[0px_1px_0px_0px_#FFFFFF0A_inset,0px_1px_3px_0px_#0000001A]',
                ]
              : 'bg-background-blur-bold',
          )}
        >
          <CardContent className="flex flex-col gap-4 p-6">
            <div className="flex flex-col gap-1">
              <span
                data-testid="home-trackProgress-sectionLabel"
                className={cn(
                  'text-xs tracking-widest uppercase',
                  isRetestMode ? 'text-[#C8CFDB80]' : 'text-secondary',
                )}
              >
                Test Status
              </span>
              <span
                data-testid="home-trackProgress-statusHeader"
                className={cn(
                  'typography-h2 font-bold mt-1 mb-2',
                  isRetestMode ? 'text-[#C8CFDBCC]' : 'text-primary',
                )}
              >
                {testStatus.status_header}
              </span>
              <span
                data-testid="home-trackProgress-statusSubheader"
                className={cn(
                  'typography-body',
                  isRetestMode ? 'text-[#C8CFDB80]' : 'text-secondary',
                )}
              >
                {testStatus.status_subheader}
              </span>
            </div>

            <StepProgressBar
              steps={testStatus?.track_status_list ?? []}
              currentStepIndex={currentStepIndex}
              hasError={issueInCurrentStatus}
              isRetestMode={isRetestMode}
            />

            {testStatus.eta_display_text && (
              <div
                data-testid="home-trackProgress-eta"
                className={cn(
                  'rounded-2xl px-5 py-4 flex flex-col gap-1.5',
                  isRetestMode ? 'bg-white/5' : 'bg-background-secondary',
                )}
              >
                <span
                  className={cn(
                    'text-xs font-semibold tracking-wide',
                    isRetestMode ? 'text-[#C8CFDB80]' : 'text-secondary',
                  )}
                >
                  ESTIMATED RESULTS
                </span>
                <span
                  className={cn(
                    'typography-h3 font-semibold',
                    testStatus.is_eta_over_ceiling
                      ? 'text-amber-500'
                      : isRetestMode
                        ? 'text-[#C8CFDBCC]'
                        : 'text-primary',
                  )}
                >
                  {testStatus.eta_display_text}
                </span>
              </div>
            )}

            {testStatus.lab_process && (
              <button
                data-testid="home-trackProgress-labProcess"
                className="flex items-center gap-1 mt-1 text-tertiary"
                onClick={(e) => {
                  e.stopPropagation();
                  EventTracker.trackEvent(
                    'home',
                    'sample_tracker_learn_more_tap',
                  );
                  setIsLabProcessOpen(true);
                }}
              >
                <span className="typography-body text-sm underline">
                  Learn about the lab process
                </span>
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </CardContent>
        </Card>
      </div>

      {testStatus.lab_process && (
        <LabProcessSheet
          labProcess={testStatus.lab_process}
          open={isLabProcessOpen}
          onOpenChange={setIsLabProcessOpen}
        />
      )}
    </>
  );
};
